import * as React from 'react';

import cn from 'classnames';
import { findIndex } from 'lodash';

import { GetProductsByIdBatchedQuery } from '@notino/shared/definitions/types';

import { ProductSlider } from '@components/ProductSlider';
import { useGetProductItemProps } from '@components/Universals/ProductItem/useGetProductItemProps';
import { IProduct } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import {
  useTrackDynamicList,
  ProductEventWither,
} from '@helpers/googleTagManager';
import { RecommendationPlacement } from '@utils/tracking/recommendationPlacement';
import { RecommendationAttributesInput } from '@utils/tracking/recommendationTracking';
import { useRecommendationTracking } from '@utils/tracking/useRecommendationTracking';

import { ProductsSectionPlaceholder } from '../ProductsSectionPlaceholder';

import { BULK_CALL_TIMEOUT } from './constants';
import {
  legacyDiscoveryCardClass,
  newDiscoveryCardClass,
  SliderWrapper,
} from './styled';

export interface IProductSectionContentProps {
  listLabel?: string;
  trackProductClick?: boolean;
  trackProductImpression?: boolean;
  data: GetProductsByIdBatchedQuery;
  itemsPerSlide: number;
  attributionToken?: string;
  className?: string;
  isSponsored?: boolean;
  onProductClick?: (productId: string) => void;
  onProductImpression?: (productId: string) => void;
  onProductBasketChange?: (productId: string, quantity: number) => void;
  onProductWishlist?: (productId: string, wishlisted: boolean) => void;
  recommendationPlacement?: RecommendationPlacement;
  recommendationAttributes?: RecommendationAttributesInput;
}

export const ProductsSectionContent: React.FC<IProductSectionContentProps> =
  React.memo(
    ({
      listLabel = '',
      trackProductClick = false,
      trackProductImpression = false,
      data,
      itemsPerSlide,
      attributionToken,
      className,
      isSponsored = false,
      onProductClick,
      onProductImpression,
      onProductBasketChange,
      onProductWishlist,
      recommendationPlacement,
      recommendationAttributes,
    }) => {
      const trackVisibleItems = useTrackDynamicList({
        delay: BULK_CALL_TIMEOUT,
        listName: listLabel,
      });

      const { release_discovery_product_card_2026 } = useFeatureFlags();

      const recoTracking = useRecommendationTracking(
        recommendationPlacement,
        recommendationAttributes
      );

      const getRecoProduct = React.useCallback(
        (id: IProduct['id']) => {
          const product = data.vpProductByIds.find(
            (p): p is NonNullable<typeof p> => p?.id === id
          );
          if (!product?.productCode || !product.masterProductCode) return null;
          return {
            productCode: product.productCode,
            productMasterCode: product.masterProductCode,
          };
        },
        [data.vpProductByIds]
      );

      React.useEffect(() => {
        if (!recommendationPlacement) return;
        const recoProducts = data.vpProductByIds
          .map((p) =>
            p?.productCode && p.masterProductCode
              ? {
                  productCode: p.productCode,
                  productMasterCode: p.masterProductCode,
                }
              : null
          )
          .filter((p): p is NonNullable<typeof p> => p !== null);
        recoTracking.trackShow(recoProducts);
      }, [recommendationPlacement, data.vpProductByIds, recoTracking]);

      const onNewItemShow = (id: IProduct['id']) => {
        if (trackProductImpression && listLabel) {
          const index = findIndex(data.vpProductByIds, { id });
          if (index !== -1) {
            const product = data.vpProductByIds[index];
            if (!product) return;
            trackVisibleItems(
              ProductEventWither()
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .withVpProduct(product as any)
                .withDiscountPrices(product.attributes, {
                  shouldSendOnlyDiscountPrice: true,
                })
                .withAdditionalData({
                  list_position: index + 1,
                  id: product.id ?? '',
                  list_name: isSponsored ? `${listLabel}_sponsored` : listLabel,
                })
                .build()
            );
          }
        }
        const recoProduct = getRecoProduct(id);
        if (recoProduct) recoTracking.trackInView(recoProduct);
        onProductImpression?.(id);
      };

      const getProductItemProps = useGetProductItemProps({
        noPriceIfUnavailable: true,
        inSectionOfType: 'slider',
        GTMEventLabel: isSponsored ? `${listLabel}_sponsored` : listLabel,
        sendGTM: trackProductClick,
        wishlistActionLocation: 'list',
        setProductWishlisted: onProductWishlist
          ? (productId, wishlisted) =>
              onProductWishlist(String(productId), wishlisted)
          : undefined,
        onProductAdded: onProductBasketChange
          ? (product) => {
              if (product?.id) {
                onProductBasketChange(String(product.id), 1);
              }
            }
          : undefined,
      });

      const products = data.vpProductByIds
        .filter(
          (product): product is NonNullable<typeof product> => product !== null
        )
        .map((product, index) => {
          const props = getProductItemProps({
            product,
            position: index + 1,
            attributionToken,
            isSponsored,
          });
          if (!props) return props;
          const originalOnClick = props.onClick;
          const recoProduct = getRecoProduct(product.id!);
          if (!onProductClick && !recoProduct) return props;
          return {
            ...props,
            onClick: (
              ...args: Parameters<NonNullable<typeof originalOnClick>>
            ) => {
              if (recoProduct) recoTracking.trackClick(recoProduct);
              onProductClick?.(product.id!);
              originalOnClick?.(...args);
            },
          };
        })
        .filter((p): p is NonNullable<typeof p> => Boolean(p));

      return (
        <SliderWrapper
          className={cn(
            className,
            release_discovery_product_card_2026
              ? newDiscoveryCardClass
              : legacyDiscoveryCardClass
          )}
        >
          <ProductSlider
            itemsPerSlide={itemsPerSlide}
            inViewThreshold={0.55}
            onItemShow={({ product }) => {
              onNewItemShow(product.id);
            }}
            products={products}
            fallback={
              <ProductsSectionPlaceholder
                itemsPerSlide={itemsPerSlide}
                listLabel={listLabel}
              />
            }
          />
        </SliderWrapper>
      );
    }
  );
